import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Award, Plus, Search, Edit2, Trash2, Check, X, Loader2, Tag, Clock, AlertTriangle, EyeOff, RotateCcw, Merge, GraduationCap, ChevronDown, ChevronRight } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

interface CredentialType {
  id: string
  name: string
  abbreviation: string | null
  issuingAuthorityType: string
  issuingAuthority: string | null
  requiresState: boolean
  category: string | null
  categories: string[] | null
  keywords: string[] | null
  isApproved: boolean
  isProfession: boolean
  createdAt: string
}

interface PendingRequest {
  id: string
  certName: string
  requestCount: number
  firstRequested: string
  isIgnored: boolean
  certificationIds: string[]
}

interface CleanupItem {
  certName: string
  count: number
  certificationIds: string[]
}

interface IgnoredCleanupItem {
  id: number
  certName: string
  ignoredAt: string
}

interface CeRequirement {
  id: string
  professionId: string
  state: string
  totalHours: string | null
  renewalPeriodYears: number | null
  professionName: string
  followsNremt: boolean
}

interface CeCategory {
  id: string
  requirementId: string
  name: string
  requiredHours: string | null
  isPediatricContent: boolean
  isOneTime: boolean
}

interface CategoryAllocation {
  categoryName: string
  hours: string
}

interface CeCredit {
  id: string
  ceRequirementId: string
  credentialTypeId: string
  allocationMode: string
  totalHours: string
  professionName: string
  state: string
  categories: Array<{
    categoryId: string
    categoryName: string
    fixedHours: string
  }>
}

type TabType = 'approved' | 'pending' | 'cleanup' | 'ignored'

export default function LicensesPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabType>('approved')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editAbbreviation, setEditAbbreviation] = useState('')
  const [editIssuingAuthorityType, setEditIssuingAuthorityType] = useState('national')
  const [editIssuingAuthority, setEditIssuingAuthority] = useState('')
  const [editRequiresState, setEditRequiresState] = useState(false)
  const [editCategory, setEditCategory] = useState('')
  const [editIsProfession, setEditIsProfession] = useState(false)
  const [editKeywords, setEditKeywords] = useState('')
  const [newName, setNewName] = useState('')
  const [newKeywords, setNewKeywords] = useState('')
  const [showMergeModal, setShowMergeModal] = useState<{ certName: string; ids: string[] } | null>(null)
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null)
  const [showCeCreditsModal, setShowCeCreditsModal] = useState<CredentialType | null>(null)
  const [ceCreditsHours, setCeCreditsHours] = useState('')
  const [ceCreditsMode, setCeCreditsMode] = useState<'flexible' | 'fixed'>('flexible')
  const [selectedRequirements, setSelectedRequirements] = useState<Set<string>>(new Set())
  const [availableCategories, setAvailableCategories] = useState<CeCategory[]>([])
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [categoryHours, setCategoryHours] = useState<Record<string, string>>({})
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [expandedProfessions, setExpandedProfessions] = useState<Set<string>>(new Set())

  // Fetch approved credential types
  const { data: typesData, isLoading: typesLoading } = useQuery({
    queryKey: ['admin', 'credential-types'],
    queryFn: () => apiGet<{ credentialTypes: CredentialType[] }>('/admin/credential-types'),
  })

  // Fetch pending requests
  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['admin', 'credential-requests'],
    queryFn: () => apiGet<{ requests: PendingRequest[] }>('/admin/credential-requests?status=pending'),
    enabled: activeTab === 'pending',
  })

  // Fetch cleanup items
  const { data: cleanupData, isLoading: cleanupLoading } = useQuery({
    queryKey: ['admin', 'cleanup'],
    queryFn: () => apiGet<{ unmatchedCerts: CleanupItem[] }>('/admin/cleanup'),
    enabled: activeTab === 'cleanup',
  })

  // Fetch ignored items (both pending and cleanup)
  const { data: ignoredPendingData, isLoading: ignoredPendingLoading } = useQuery({
    queryKey: ['admin', 'credential-requests', 'ignored'],
    queryFn: () => apiGet<{ requests: PendingRequest[] }>('/admin/credential-requests/ignored'),
    enabled: activeTab === 'ignored',
  })

  const { data: ignoredCleanupData, isLoading: ignoredCleanupLoading } = useQuery({
    queryKey: ['admin', 'cleanup', 'ignored'],
    queryFn: () => apiGet<{ ignoredItems: IgnoredCleanupItem[] }>('/admin/cleanup/ignored'),
    enabled: activeTab === 'ignored',
  })

  // Fetch all CE requirements for bulk apply
  const { data: ceRequirementsData } = useQuery({
    queryKey: ['admin', 'ce-requirements', 'all'],
    queryFn: () => apiGet<{ requirements: CeRequirement[] }>('/admin/ce-requirements'),
    enabled: !!showCeCreditsModal,
  })

  // Fetch existing CE credits for selected credential type
  const { data: existingCreditsData, isLoading: loadingExistingCredits } = useQuery({
    queryKey: ['admin', 'credential-types', showCeCreditsModal?.id, 'ce-credits'],
    queryFn: () => apiGet<{ credits: CeCredit[] }>(`/admin/credential-types/${showCeCreditsModal?.id}/ce-credits`),
    enabled: !!showCeCreditsModal,
  })

  // Mutations for approved types
  const createMutation = useMutation({
    mutationFn: (data: { name: string; keywords?: string[]; issuingAuthorityType?: string }) =>
      apiPost('/admin/credential-types', { ...data, issuingAuthorityType: data.issuingAuthorityType || 'national' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types'] })
      setShowAddModal(false)
      setNewName('')
      setNewKeywords('')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { 
      name: string
      abbreviation?: string | null
      issuingAuthorityType?: string
      issuingAuthority?: string | null
      requiresState?: boolean
      category?: string | null
      isProfession?: boolean
      keywords?: string[] 
    } }) =>
      apiPut(`/admin/credential-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types'] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/credential-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types'] })
    },
  })

  // Mutations for pending requests
  const approvePendingMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/credential-requests/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types'] })
    },
  })

  const rejectPendingMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/credential-requests/${id}/reject`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests'] })
    },
  })

  const ignorePendingMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/credential-requests/${id}/ignore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests'] })
    },
  })

  const unignorePendingMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/credential-requests/${id}/unignore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests', 'ignored'] })
    },
  })

  const mergePendingMutation = useMutation({
    mutationFn: ({ id, targetTypeId }: { id: string; targetTypeId: string }) =>
      apiPost(`/admin/credential-requests/${id}/merge`, { targetTypeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-requests'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'cleanup'] })
      setShowMergeModal(null)
      setSelectedTypeId(null)
    },
  })

  // Mutations for cleanup
  const remapCleanupMutation = useMutation({
    mutationFn: ({ certName, targetTypeId }: { certName: string; targetTypeId: string }) =>
      apiPost('/admin/cleanup/remap', { certName, targetTypeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cleanup'] })
      setShowMergeModal(null)
      setSelectedTypeId(null)
    },
  })

  const ignoreCleanupMutation = useMutation({
    mutationFn: (certName: string) => apiPost('/admin/cleanup/ignore', { certName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cleanup'] })
    },
  })

  const unignoreCleanupMutation = useMutation({
    mutationFn: (certName: string) => apiPost('/admin/cleanup/unignore', { certName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cleanup', 'ignored'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'cleanup'] })
    },
  })

  // Mutation for bulk apply CE credits
  const bulkApplyCeCredits = useMutation({
    mutationFn: ({ credentialTypeId, ceRequirementIds, totalHours, allocationMode, categoryAllocations }: {
      credentialTypeId: string
      ceRequirementIds: string[]
      totalHours: string
      allocationMode: string
      categoryAllocations?: CategoryAllocation[]
    }) => apiPost(`/admin/credential-types/${credentialTypeId}/ce-credits/bulk`, {
      ceRequirementIds,
      totalHours,
      allocationMode,
      categoryAllocations,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types', showCeCreditsModal?.id, 'ce-credits'] })
      setSelectedRequirements(new Set())
      setCeCreditsHours('')
      setCeCreditsMode('flexible')
      setAvailableCategories([])
      setSelectedCategories(new Set())
      setCategoryHours({})
    },
  })

  // Mutation for deleting CE credit
  const deleteCeCredit = useMutation({
    mutationFn: ({ credentialTypeId, creditId }: { credentialTypeId: string; creditId: string }) =>
      apiDelete(`/admin/credential-types/${credentialTypeId}/ce-credits/${creditId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types', showCeCreditsModal?.id, 'ce-credits'] })
    },
  })

  const resetAllCeCredits = useMutation({
    mutationFn: async (credentialTypeId: string) => {
      // Delete all credits for this credential type one by one
      const credits = existingCreditsData?.credits || []
      for (const credit of credits) {
        await apiDelete(`/admin/credential-types/${credentialTypeId}/ce-credits/${credit.id}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credential-types', showCeCreditsModal?.id, 'ce-credits'] })
    },
  })

  const credentialTypes = typesData?.credentialTypes || []
  const pendingRequests = pendingData?.requests?.filter(r => !r.isIgnored) || []
  const cleanupItems = cleanupData?.unmatchedCerts || []
  const ceRequirements = ceRequirementsData?.requirements || []
  const existingCredits = existingCreditsData?.credits || []

  // Group CE requirements by profession for easy selection
  const requirementsByProfession = useMemo(() => {
    const grouped: Record<string, { professionName: string; professionId: string; requirements: CeRequirement[] }> = {}
    for (const req of ceRequirements) {
      if (!grouped[req.professionId]) {
        grouped[req.professionId] = { professionName: req.professionName, professionId: req.professionId, requirements: [] }
      }
      grouped[req.professionId].requirements.push(req)
    }
    // Sort each profession's requirements by state, with NREMT at top
    Object.values(grouped).forEach(group => {
      group.requirements.sort((a, b) => {
        if (a.state === 'NREMT') return -1
        if (b.state === 'NREMT') return 1
        return a.state.localeCompare(b.state)
      })
    })
    return Object.values(grouped).sort((a, b) => a.professionName.localeCompare(b.professionName))
  }, [ceRequirements])

  // Check which requirements already have CE credits for this credential type
  const existingCreditRequirementIds = useMemo(() => {
    return new Set(existingCredits.map(c => c.ceRequirementId))
  }, [existingCredits])
  const ignoredPending = ignoredPendingData?.requests || []
  const ignoredCleanup = ignoredCleanupData?.ignoredItems || []

  const filteredTypes = credentialTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.keywords?.some((k) => k.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleCreate = () => {
    const keywords = newKeywords.split(',').map((k) => k.trim()).filter(Boolean)
    createMutation.mutate({ name: newName, keywords: keywords.length > 0 ? keywords : undefined })
  }

  const handleUpdate = (id: string) => {
    const keywords = editKeywords.split(',').map((k) => k.trim()).filter(Boolean)
    updateMutation.mutate({ 
      id, 
      data: { 
        name: editName, 
        abbreviation: editAbbreviation || null,
        issuingAuthorityType: editIssuingAuthorityType,
        issuingAuthority: editIssuingAuthority || null,
        requiresState: editRequiresState,
        category: editCategory || null,
        isProfession: editIsProfession,
        keywords: keywords.length > 0 ? keywords : undefined 
      } 
    })
  }

  const startEdit = (type: CredentialType) => {
    setEditingId(type.id)
    setEditName(type.name)
    setEditAbbreviation(type.abbreviation || '')
    setEditIssuingAuthorityType(type.issuingAuthorityType || 'national')
    setEditIssuingAuthority(type.issuingAuthority || '')
    setEditRequiresState(type.requiresState || false)
    setEditCategory(type.category || '')
    setEditIsProfession(type.isProfession || false)
    setEditKeywords(type.keywords?.join(', ') || '')
  }

  const openCeCreditsModal = (type: CredentialType) => {
    setShowCeCreditsModal(type)
    setCeCreditsHours('')
    setCeCreditsMode('flexible')
    setSelectedRequirements(new Set())
    setAvailableCategories([])
    setSelectedCategories(new Set())
    setCategoryHours({})
    setExpandedProfessions(new Set())
  }

  const toggleProfessionExpanded = (professionId: string) => {
    setExpandedProfessions(prev => {
      const next = new Set(prev)
      if (next.has(professionId)) {
        next.delete(professionId)
      } else {
        next.add(professionId)
      }
      return next
    })
  }

  const fetchCategoriesForRequirement = async (requirementId: string) => {
    try {
      const response = await apiGet<{ categories: CeCategory[] }>(`/admin/ce-requirements/${requirementId}/categories`)
      return response.categories || []
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      return []
    }
  }

  const updateCategoriesFromSelectedRequirements = async (reqIds: Set<string>) => {
    if (reqIds.size === 0) {
      setAvailableCategories([])
      setSelectedCategories(new Set())
      setCategoryHours({})
      return
    }

    setLoadingCategories(true)
    try {
      const firstReqId = Array.from(reqIds)[0]
      const categories = await fetchCategoriesForRequirement(firstReqId)
      setAvailableCategories(categories)
      setSelectedCategories(new Set())
      setCategoryHours({})
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
    setLoadingCategories(false)
  }

  const handleBulkApply = () => {
    if (!showCeCreditsModal || selectedRequirements.size === 0 || !ceCreditsHours) return
    
    const categoryAllocations: CategoryAllocation[] = []
    if (selectedCategories.size > 0) {
      selectedCategories.forEach(catName => {
        const hours = categoryHours[catName] || '0'
        categoryAllocations.push({ categoryName: catName, hours })
      })
    }
    
    bulkApplyCeCredits.mutate({
      credentialTypeId: showCeCreditsModal.id,
      ceRequirementIds: Array.from(selectedRequirements),
      totalHours: ceCreditsHours,
      allocationMode: ceCreditsMode,
      categoryAllocations: categoryAllocations.length > 0 ? categoryAllocations : undefined,
    })
  }

  const toggleRequirement = (reqId: string, professionReqs?: CeRequirement[]) => {
    const next = new Set(selectedRequirements)
    const toggledReq = ceRequirements.find(r => r.id === reqId)
    
    if (next.has(reqId)) {
      next.delete(reqId)
      // If deselecting NREMT, also deselect states that follow NREMT in this profession
      if (toggledReq?.state === 'NREMT' && professionReqs) {
        professionReqs
          .filter(r => r.followsNremt && r.id !== reqId)
          .forEach(r => next.delete(r.id))
      }
    } else {
      next.add(reqId)
      // If selecting NREMT, also select states that follow NREMT in this profession
      if (toggledReq?.state === 'NREMT' && professionReqs) {
        professionReqs
          .filter(r => r.followsNremt && r.id !== reqId)
          .forEach(r => next.add(r.id))
      }
    }
    setSelectedRequirements(next)
    updateCategoriesFromSelectedRequirements(next)
  }

  const toggleProfession = (professionReqs: CeRequirement[]) => {
    const reqIds = professionReqs.map(r => r.id)
    const next = new Set(selectedRequirements)
    const allSelected = reqIds.every(id => next.has(id))
    if (allSelected) {
      reqIds.forEach(id => next.delete(id))
    } else {
      reqIds.forEach(id => next.add(id))
    }
    setSelectedRequirements(next)
    updateCategoriesFromSelectedRequirements(next)
  }

  const selectAll = () => {
    const allIds = ceRequirements.map(r => r.id)
    const newSet = new Set(allIds)
    setSelectedRequirements(newSet)
    updateCategoriesFromSelectedRequirements(newSet)
  }

  const selectNone = () => {
    setSelectedRequirements(new Set())
    setAvailableCategories([])
    setSelectedCategories(new Set())
    setCategoryHours({})
  }

  const toggleCategory = (categoryName: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryName)) {
        next.delete(categoryName)
      } else {
        next.add(categoryName)
      }
      return next
    })
  }

  const updateCategoryHours = (categoryName: string, hours: string) => {
    setCategoryHours(prev => ({ ...prev, [categoryName]: hours }))
  }

  const tabs = [
    { id: 'approved' as const, label: 'Approved', count: credentialTypes.length },
    { id: 'pending' as const, label: 'Pending', count: pendingRequests.length },
    { id: 'cleanup' as const, label: 'Cleanup', count: cleanupItems.length },
    { id: 'ignored' as const, label: 'Ignored', count: ignoredPending.length + ignoredCleanup.length },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">License Types</h1>
          <p className="text-gray-500 mt-1">Manage approved credential and license types</p>
        </div>
        {activeTab === 'approved' && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Type
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Search - only for approved tab */}
      {activeTab === 'approved' && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Approved Types Tab */}
      {activeTab === 'approved' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {typesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredTypes.length === 0 ? (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No license types found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTypes.map((type) => (
                <div 
                  key={type.id} 
                  className={`p-4 hover:bg-gray-50 ${type.isProfession ? 'border-l-4 border-l-green-500 bg-green-50' : ''}`}
                >
                  {editingId === type.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="License name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation</label>
                          <input
                            type="text"
                            value={editAbbreviation}
                            onChange={(e) => setEditAbbreviation(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., RN, EMT"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Authority Type</label>
                          <select
                            value={editIssuingAuthorityType}
                            onChange={(e) => setEditIssuingAuthorityType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="national">National</option>
                            <option value="state">State</option>
                            <option value="organization">Organization</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Authority</label>
                          <input
                            type="text"
                            value={editIssuingAuthority}
                            onChange={(e) => setEditIssuingAuthority(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Texas Board of Nursing"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <input
                          type="text"
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Nursing, EMS, Allied Health"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
                        <input
                          type="text"
                          value={editKeywords}
                          onChange={(e) => setEditKeywords(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., CPR, cardiac"
                        />
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editRequiresState}
                            onChange={(e) => setEditRequiresState(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Requires State</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editIsProfession}
                            onChange={(e) => setEditIsProfession(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Is Profession</span>
                        </label>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleUpdate(type.id)}
                          disabled={updateMutation.isPending}
                          className="inline-flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                        >
                          <Check className="w-4 h-4" />
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="inline-flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-gray-900">
                            {type.name}
                            {type.abbreviation && (
                              <span className="text-gray-500 font-normal"> ({type.abbreviation})</span>
                            )}
                          </h3>
                          {type.isProfession && (
                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full font-medium">
                              Profession
                            </span>
                          )}
                          {type.isApproved && !type.isProfession && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              Approved
                            </span>
                          )}
                          {type.requiresState && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              State Required
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
                          {type.issuingAuthority && (
                            <span>Issued by: {type.issuingAuthority}</span>
                          )}
                          {type.category && (
                            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                              {type.category}
                            </span>
                          )}
                        </div>
                        {type.keywords && type.keywords.length > 0 && (
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <Tag className="w-3 h-3 text-gray-400" />
                            {type.keywords.map((keyword, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => openCeCreditsModal(type)}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Configure CE Credits"
                        >
                          <GraduationCap className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(type)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${type.name}"? This cannot be undone.`)) {
                              deleteMutation.mutate(type.id)
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {pendingLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No pending requests</p>
              <p className="text-sm text-gray-400 mt-1">New credential types requested by users will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingRequests.map((request) => (
                <div key={request.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{request.certName}</h3>
                      <p className="text-sm text-gray-500">
                        Requested {request.requestCount} time{request.requestCount > 1 ? 's' : ''} • 
                        First requested {new Date(request.firstRequested).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => approvePendingMutation.mutate(request.id)}
                        disabled={approvePendingMutation.isPending}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 inline-flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => setShowMergeModal({ certName: request.certName, ids: [request.id] })}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 text-sm rounded-lg hover:bg-blue-100 inline-flex items-center gap-1"
                      >
                        <Merge className="w-4 h-4" />
                        Merge
                      </button>
                      <button
                        onClick={() => ignorePendingMutation.mutate(request.id)}
                        disabled={ignorePendingMutation.isPending}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 inline-flex items-center gap-1"
                      >
                        <EyeOff className="w-4 h-4" />
                        Ignore
                      </button>
                      <button
                        onClick={() => rejectPendingMutation.mutate(request.id)}
                        disabled={rejectPendingMutation.isPending}
                        className="px-3 py-1.5 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 inline-flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cleanup Tab */}
      {activeTab === 'cleanup' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {cleanupLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : cleanupItems.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No unmatched certifications</p>
              <p className="text-sm text-gray-400 mt-1">All user certifications match approved types</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {cleanupItems.map((item) => (
                <div key={item.certName} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{item.certName}</h3>
                      <p className="text-sm text-gray-500">
                        Found in {item.count} certification{item.count > 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowMergeModal({ certName: item.certName, ids: item.certificationIds })}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 inline-flex items-center gap-1"
                      >
                        <Merge className="w-4 h-4" />
                        Remap
                      </button>
                      <button
                        onClick={() => ignoreCleanupMutation.mutate(item.certName)}
                        disabled={ignoreCleanupMutation.isPending}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 inline-flex items-center gap-1"
                      >
                        <EyeOff className="w-4 h-4" />
                        Ignore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ignored Tab */}
      {activeTab === 'ignored' && (
        <div className="space-y-6">
          {/* Ignored Pending Requests */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Ignored Pending Requests</h2>
            </div>
            {ignoredPendingLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : ignoredPending.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No ignored pending requests</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {ignoredPending.map((request) => (
                  <div key={request.id} className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{request.certName}</h3>
                      <p className="text-sm text-gray-500">
                        {request.requestCount} request{request.requestCount > 1 ? 's' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => unignorePendingMutation.mutate(request.id)}
                      disabled={unignorePendingMutation.isPending}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 inline-flex items-center gap-1"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ignored Cleanup Items */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Ignored Cleanup Items</h2>
            </div>
            {ignoredCleanupLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : ignoredCleanup.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No ignored cleanup items</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {ignoredCleanup.map((item) => (
                  <div key={item.id} className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{item.certName}</h3>
                      <p className="text-sm text-gray-500">
                        Ignored {new Date(item.ignoredAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => unignoreCleanupMutation.mutate(item.certName)}
                      disabled={unignoreCleanupMutation.isPending}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 inline-flex items-center gap-1"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Type Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add License Type</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Basic Life Support (BLS)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (optional)</label>
                <input
                  type="text"
                  value={newKeywords}
                  onChange={(e) => setNewKeywords(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="CPR, First Aid (comma-separated)"
                />
                <p className="text-xs text-gray-500 mt-1">Keywords help match user entries to this type</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewName('')
                  setNewKeywords('')
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Adding...' : 'Add Type'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge/Remap Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {activeTab === 'cleanup' ? 'Remap' : 'Merge'} to Approved Type
            </h2>
            <p className="text-gray-500 mb-4">
              Select an approved type to map "{showMergeModal.certName}" to:
            </p>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {credentialTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedTypeId(type.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 ${
                    selectedTypeId === type.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <span className={selectedTypeId === type.id ? 'text-blue-700 font-medium' : 'text-gray-900'}>
                    {type.name}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMergeModal(null)
                  setSelectedTypeId(null)
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedTypeId && showMergeModal) {
                    if (activeTab === 'cleanup') {
                      remapCleanupMutation.mutate({ certName: showMergeModal.certName, targetTypeId: selectedTypeId })
                    } else {
                      mergePendingMutation.mutate({ id: showMergeModal.ids[0], targetTypeId: selectedTypeId })
                    }
                  }
                }}
                disabled={!selectedTypeId || mergePendingMutation.isPending || remapCleanupMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {mergePendingMutation.isPending || remapCleanupMutation.isPending ? 'Processing...' : activeTab === 'cleanup' ? 'Remap' : 'Merge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CE Credits Modal */}
      {showCeCreditsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">CE Credits for {showCeCreditsModal.name}</h2>
                  <p className="text-gray-500 mt-1">Configure which professions receive CE hours for holding this certification</p>
                </div>
                <button
                  onClick={() => setShowCeCreditsModal(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Existing Credits Section */}
              {existingCredits.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">Currently Applied ({existingCredits.length})</h3>
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove ALL ${existingCredits.length} CE credit assignments for ${showCeCreditsModal.name}? This cannot be undone.`)) {
                          resetAllCeCredits.mutate(showCeCreditsModal.id)
                        }
                      }}
                      disabled={resetAllCeCredits.isPending}
                      className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg border border-red-200 inline-flex items-center gap-1.5"
                    >
                      {resetAllCeCredits.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                      Reset All
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                    {existingCredits.map((credit) => (
                      <div key={credit.id} className="p-3 flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">{credit.professionName}</span>
                          <span className="text-gray-500 ml-2">- {credit.state}</span>
                          <span className="ml-3 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                            {credit.totalHours} hrs ({credit.allocationMode})
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove CE credit for ${credit.professionName} - ${credit.state}?`)) {
                              deleteCeCredit.mutate({ credentialTypeId: showCeCreditsModal.id, creditId: credit.id })
                            }
                          }}
                          disabled={deleteCeCredit.isPending}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bulk Apply Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Bulk Apply CE Credits</h3>
                
                {/* Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={ceCreditsHours}
                      onChange={(e) => setCeCreditsHours(e.target.value)}
                      placeholder="e.g., 4"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allocation Mode</label>
                    <select
                      value={ceCreditsMode}
                      onChange={(e) => setCeCreditsMode(e.target.value as 'flexible' | 'fixed')}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="flexible">Flexible (User allocates hours)</option>
                      <option value="fixed">Fixed (Admin sets exact hours per category)</option>
                    </select>
                  </div>
                </div>

                {/* Category Selection - only show if requirements selected and categories loaded */}
                {availableCategories.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      {ceCreditsMode === 'fixed' 
                        ? 'Select Categories & Assign Hours (Fixed Mode)' 
                        : 'Limit to Specific Categories (Flexible Mode - Optional)'}
                    </h4>
                    <p className="text-xs text-blue-700 mb-3">
                      {ceCreditsMode === 'fixed'
                        ? 'Select which categories receive CE hours and specify the hours for each.'
                        : 'Optionally limit which categories users can allocate their hours to. Leave all unchecked to allow allocation to any category.'}
                    </p>
                    <div className="space-y-2">
                      {availableCategories.map((cat) => (
                        <div key={cat.id} className="flex items-center gap-3 p-2 bg-white rounded border border-blue-100">
                          <input
                            type="checkbox"
                            checked={selectedCategories.has(cat.name)}
                            onChange={() => toggleCategory(cat.name)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="flex-1 text-sm text-gray-700">
                            {cat.name}
                            {cat.isPediatricContent && <span className="ml-1 text-xs text-purple-600">(Pediatric)</span>}
                            {cat.isOneTime && <span className="ml-1 text-xs text-orange-600">(One-time)</span>}
                            {cat.requiredHours && <span className="ml-1 text-xs text-gray-500">({cat.requiredHours}hrs req)</span>}
                          </span>
                          {ceCreditsMode === 'fixed' && selectedCategories.has(cat.name) && (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={categoryHours[cat.name] || ''}
                                onChange={(e) => updateCategoryHours(cat.name, e.target.value)}
                                placeholder="Hours"
                                className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <span className="text-xs text-gray-500">hrs</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {ceCreditsMode === 'fixed' && selectedCategories.size > 0 && (
                      <div className="mt-2 text-xs text-blue-700">
                        Total assigned: {Array.from(selectedCategories).reduce((sum, cat) => sum + (parseFloat(categoryHours[cat]) || 0), 0)} hrs 
                        (should match Total Hours: {ceCreditsHours || '0'} hrs)
                      </div>
                    )}
                  </div>
                )}

                {loadingCategories && (
                  <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading categories...
                  </div>
                )}

                {selectedRequirements.size > 0 && !loadingCategories && availableCategories.length === 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    No categories defined for the selected profession/state. Hours will be awarded as general CE hours.
                  </div>
                )}

                {/* Quick Actions */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={selectAll}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Select All
                  </button>
                  <button
                    onClick={selectNone}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Select None
                  </button>
                  <span className="text-sm text-gray-500 ml-2 self-center">
                    {selectedRequirements.size} selected
                  </span>
                </div>

                {/* Profession/State Selection */}
                {loadingExistingCredits ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : requirementsByProfession.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No CE requirements found. Add professions and their CE requirements in the CE Management page first.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    {requirementsByProfession.map((group) => {
                      const allSelected = group.requirements.every(r => selectedRequirements.has(r.id))
                      const someSelected = group.requirements.some(r => selectedRequirements.has(r.id))
                      const isExpanded = expandedProfessions.has(group.professionId)
                      const sortedRequirements = [...group.requirements].sort((a, b) => {
                        if (a.state === 'NREMT') return -1
                        if (b.state === 'NREMT') return 1
                        return a.state.localeCompare(b.state)
                      })
                      return (
                        <div key={group.professionName} className="border-b border-gray-100 last:border-0">
                          <div
                            className="px-4 py-3 bg-gray-50 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleProfessionExpanded(group.professionId) }}
                              className="p-0.5 hover:bg-gray-200 rounded"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                            </button>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected }}
                              onChange={(e) => { e.stopPropagation(); toggleProfession(group.requirements) }}
                              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                            />
                            <span onClick={() => toggleProfessionExpanded(group.professionId)} className="flex-1">
                              {group.professionName}
                              <span className="text-sm text-gray-500 font-normal ml-1">({group.requirements.length} states)</span>
                            </span>
                          </div>
                          {isExpanded && (
                            <div className="pl-8 divide-y divide-gray-50">
                              {sortedRequirements.map((req) => {
                                const hasExisting = existingCreditRequirementIds.has(req.id)
                                const isNremt = req.state === 'NREMT'
                                const followsNremt = req.followsNremt
                                return (
                                  <label
                                    key={req.id}
                                    className={`flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-50 ${hasExisting ? 'opacity-60' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedRequirements.has(req.id)}
                                      onChange={() => toggleRequirement(req.id, sortedRequirements)}
                                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                    />
                                    <span className={`text-gray-700 ${isNremt ? 'font-semibold text-blue-700' : ''}`}>
                                      {req.state}
                                      {followsNremt && <span className="ml-1 text-xs text-blue-500">(follows NREMT)</span>}
                                    </span>
                                    {req.totalHours && (
                                      <span className="text-gray-400 text-sm">({req.totalHours} hrs required)</span>
                                    )}
                                    {hasExisting && (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded ml-auto">Already applied</span>
                                    )}
                                  </label>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Apply Button */}
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleBulkApply}
                    disabled={selectedRequirements.size === 0 || !ceCreditsHours || bulkApplyCeCredits.isPending}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {bulkApplyCeCredits.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Apply to {selectedRequirements.size} Profession/State Combinations
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
